<template>
  <v-container fluid class="pa-6 pt-3">
    <v-card-text class="pa-4 d-flex align-center flex-wrap">
      <v-text-field
        v-model="search"
        label="Search"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        clearable
        hide-details="auto"
        style="max-width: 320px"
        @click:clear="search = ''"
      />
      <v-spacer />
      <v-btn color="accent" class="white--text" @click="openDialog()">
        <v-icon left small>mdi-plus</v-icon> Add Rule
      </v-btn>
    </v-card-text>

    <v-card outlined class="mx-4 mb-6" elevation="0">
      <v-data-table
        :headers="headers"
        :items="rules"
        :search="search"
        :loading="loading"
        loading-text="Loading rules…"
        no-data-text="No visibility rules yet — the referral feature is hidden for all students."
        :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
      >
        <template #[`item.name`]="{ item }">
          {{ item.name || "-" }}
        </template>

        <template #[`item.program_name`]="{ item }">
          {{ item.program_name || "All" }}
        </template>

        <template #[`item.partner_name`]="{ item }">
          {{ item.partner_name || "All" }}
        </template>

        <template #[`item.program_batch_name`]="{ item }">
          {{ item.program_batch_name || "All" }}
        </template>

        <template #[`item.scope`]="{ item }">
          <v-tooltip bottom :disabled="!item.remarks">
            <template #activator="{ on }">
              <span v-on="on">
                {{ item.scope }}
                <v-icon v-if="item.remarks" x-small right>
                  mdi-comment-text-outline
                </v-icon>
              </span>
            </template>
            <span>{{ item.remarks }}</span>
          </v-tooltip>
        </template>

        <template #[`item.active`]="{ item }">
          <v-switch
            :key="`${item.id}-${item.active}-${switchKey}`"
            :input-value="item.active"
            :loading="togglingId === item.id"
            :disabled="togglingId === item.id"
            color="success"
            dense
            hide-details
            class="mt-0 pt-0 d-inline-flex"
            @change="confirmToggle(item, $event)"
          />
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn icon small title="Edit rule" @click="openDialog(item)">
            <v-icon small>mdi-pencil-outline</v-icon>
          </v-btn>
          <!-- <v-btn icon small title="Delete rule" @click="confirmDelete(item)">
            <v-icon small color="error">mdi-delete-outline</v-icon>
          </v-btn>-->
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" width="620" persistent>
      <v-card class="secondary">
        <v-card-title class="primary white--text">
          {{ form.id ? "Edit Rule" : "Add Rule" }}
        </v-card-title>
        <v-card-text class="pt-5">
          <v-autocomplete
            v-model="form.program_id"
            :items="programItems"
            item-text="label"
            item-value="id"
            label="Programme"
            outlined
            dense
            hide-details="auto"
            clearable
            class="mb-4"
            @change="onProgramChange"
          />

          <v-autocomplete
            v-model="form.partner_id"
            :items="partnerItems"
            item-text="name"
            item-value="id"
            label="Partner"
            outlined
            clearable
            dense
            hide-details="auto"
            class="mb-4"
          />

          <v-autocomplete
            v-model="form.program_batch_id"
            :items="batchItems"
            item-text="label"
            item-value="id"
            label="Batch"
            outlined
            dense
            clearable
            hide-details="auto"
            class="mb-4"
            @change="onBatchChange"
          />
          <v-switch
            v-model="form.active"
            label="Visible to the students matching this rule"
            color="success"
            dense
            hide-details
            class="mt-0"
          />
        </v-card-text>
        <v-card-actions class="justify-end pb-4 px-6">
          <v-btn text :disabled="saving" @click="dialog = false">Cancel</v-btn>
          <v-btn
            color="accent"
            class="white--text"
            :loading="saving"
            :disabled="remarksRule(form.remarks) !== true"
            @click="save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="toggleDialog" width="440" persistent>
      <v-card class="secondary">
        <v-card-title class="primary white--text">
          {{ toggleValue ? "Enable Rule" : "Disable Rule" }}
        </v-card-title>
        <v-card-text class="pt-5">
          <template v-if="toggleValue">
            Make the referral feature visible to the students matching
            <strong>{{ toggleTarget && toggleTarget.scope }}</strong
            >?
          </template>
          <template v-else>
            Disable the rule for
            <strong>{{ toggleTarget && toggleTarget.scope }}</strong
            >? Students covered only by this rule will stop seeing "Refer a
            Friend".
          </template>
        </v-card-text>
        <v-card-actions class="justify-end pb-4 px-6">
          <v-btn text :disabled="togglingId !== null" @click="cancelToggle">
            Cancel
          </v-btn>
          <v-btn
            :color="toggleValue ? 'success' : 'error'"
            class="white--text"
            :loading="togglingId !== null"
            @click="toggleActive"
          >
            {{ toggleValue ? "Enable" : "Disable" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" width="440" persistent>
      <v-card class="secondary">
        <v-card-title class="primary white--text">Delete Rule</v-card-title>
        <v-card-text class="pt-5">
          Delete the rule for
          <strong>{{ deleteTarget && deleteTarget.scope }}</strong
          >? Students covered only by this rule will stop seeing "Refer a
          Friend".
        </v-card-text>
        <v-card-actions class="justify-end pb-4 px-6">
          <v-btn text :disabled="deleting" @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" :loading="deleting" @click="remove">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { referralVisibilityHeaders } from "@/constants/headers";

const EXCLUDED_PROGRAMMES = [/b\.?\s*ed\b/i, /\bold\b/i];

const emptyForm = () => ({
  id: null,
  name: null,
  program_id: null,
  partner_id: null,
  program_batch_id: null,
  active: true,
  remarks: "",
});

export default {
  data() {
    return {
      headers: referralVisibilityHeaders,
      rules: [],
      partners: [],
      programs: [],
      programBatches: [],

      search: "",
      loading: false,
      saving: false,
      deleting: false,
      togglingId: null,

      toggleDialog: false,
      toggleTarget: null,
      toggleValue: false,

      switchKey: 0,

      dialog: false,
      form: emptyForm(),

      deleteDialog: false,
      deleteTarget: null,
    };
  },

  computed: {
    programItems() {
      const programs = this.programs
        .map((program) => ({
          id: program.id,
          label: this.programLabel(program),
        }))
        .filter(
          ({ label }) => !EXCLUDED_PROGRAMMES.some((re) => re.test(label)),
        );

      return [{ id: null, label: "All Programmes" }, ...programs];
    },

    partnerItems() {
      return [{ id: null, name: "All Partners" }, ...this.partners];
    },

    batchItems() {
      const batches = this.form.program_id
        ? this.programBatches.filter(
            (batch) => batch.program_id === this.form.program_id,
          )
        : this.programBatches;

      return [
        { id: null, program_id: null, label: "All Batches" },
        ...batches.map((batch) => ({
          id: batch.id,
          program_id: batch.program_id,
          label: this.form.program_id
            ? `${batch.year}-${batch.batch}`
            : batch.name || `${batch.year}-${batch.batch}`,
        })),
      ];
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Referrals", to: { name: "Referrals" } },
      { text: "Visibility Settings" },
    ]);
    await Promise.all([
      this.getPartners(),
      this.getPrograms(),
      this.getProgramBatches(),
    ]);
    await this.getRules();
  },

  methods: {
    async getRules() {
      this.loading = true;
      try {
        const res = await this.$axios.get("/referral_visibility_rules");
        this.rules = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load the visibility rules");
      } finally {
        this.loading = false;
      }
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("partners");
        this.partners = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async getPrograms() {
      try {
        const res = await this.$axios.get("/programs?active=true");
        this.programs = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load programmes");
      }
    },

    async getProgramBatches() {
      try {
        const res = await this.$axios.get("/program_batches?active=true");
        this.programBatches = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load batches");
      }
    },

    openDialog(item) {
      this.form = item
        ? {
            id: item.id,
            name: item.name,
            program_id: item.program_id,
            partner_id: item.partner_id,
            program_batch_id: item.program_batch_id,
            active: item.active,
            remarks: item.remarks || "",
          }
        : emptyForm();
      this.dialog = true;
    },

    onProgramChange() {
      const batch = this.programBatches.find(
        (item) => item.id === this.form.program_batch_id,
      );
      if (batch && batch.program_id !== this.form.program_id) {
        this.form.program_batch_id = null;
      }
    },

    onBatchChange(batchId) {
      const batch = this.programBatches.find((item) => item.id === batchId);
      if (batch) this.form.program_id = batch.program_id;
    },

    async save() {
      this.saving = true;
      const payload = {
        referral_visibility_rule: {
          name: this.form.name || null,
          program_id: this.form.program_id || null,
          partner_id: this.form.partner_id || null,
          program_batch_id: this.form.program_batch_id || null,
          active: this.form.active,
          remarks: this.form.remarks || null,
        },
      };

      try {
        if (this.form.id) {
          await this.$axios.put(
            `/referral_visibility_rules/${this.form.id}`,
            payload,
          );
        } else {
          await this.$axios.post("/referral_visibility_rules", payload);
        }
        this.$store.commit("showSnackbar", {
          message: this.form.id ? "Rule updated" : "Rule added",
          color: "success",
        });
        this.dialog = false;
        await this.getRules();
      } catch (err) {
        this.notifyError(err, "Unable to save the rule");
      } finally {
        this.saving = false;
      }
    },

    confirmToggle(item, value) {
      if (value === item.active) return;

      this.toggleTarget = item;
      this.toggleValue = value;
      this.toggleDialog = true;
    },

    cancelToggle() {
      this.toggleDialog = false;
      this.toggleTarget = null;
      this.switchKey += 1;
    },

    async toggleActive() {
      const item = this.toggleTarget;
      const value = this.toggleValue;
      if (!item) return;

      this.togglingId = item.id;
      try {
        await this.$axios.put(`/referral_visibility_rules/${item.id}`, {
          referral_visibility_rule: { active: value },
        });
        this.$set(item, "active", value);
        this.$store.commit("showSnackbar", {
          message: value ? "Rule enabled" : "Rule disabled",
          color: "success",
        });
        this.toggleDialog = false;
        this.toggleTarget = null;
      } catch (err) {
        this.notifyError(err, "Unable to change the visibility");
        this.toggleDialog = false;
        this.toggleTarget = null;
        this.switchKey += 1;
        await this.getRules();
      } finally {
        this.togglingId = null;
      }
    },

    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },

    async remove() {
      if (!this.deleteTarget) return;

      this.deleting = true;
      try {
        await this.$axios.delete(
          `/referral_visibility_rules/${this.deleteTarget.id}`,
        );
        this.$store.commit("showSnackbar", {
          message: "Rule deleted",
          color: "success",
        });
        this.deleteDialog = false;
        this.deleteTarget = null;
        await this.getRules();
      } catch (err) {
        this.notifyError(err, "Unable to delete the rule");
      } finally {
        this.deleting = false;
      }
    },

    remarksRule(value) {
      return (value || "").length <= 500 || "Max 500 characters";
    },

    programLabel(program) {
      const name = program.degree?.short_name || "";
      return (
        `${name}${
          program.specialization ? " - " + program.specialization : ""
        }`.trim() || program.code
      );
    },

    notifyError(err, fallback) {
      const data = err?.response?.data;
      const changesetError = data?.errors
        ? Object.entries(data.errors)
            .map(([field, msgs]) => `${field} ${[].concat(msgs).join(", ")}`)
            .join("; ")
        : null;

      this.$store.commit("showSnackbar", {
        message: data?.message || changesetError || fallback,
        color: "error",
      });
    },
  },
};
</script>
